import { createReducer, on } from "@ngrx/store";
import { v5 as uuidFromString } from "uuid";
import {
  CursorLocation,
  DeltaModeStateObj,
  Expression,
  GlobalSearchMatch,
  OperandType,
  OperatorType,
  SpecialCategoryLabels,
} from "../category-builder.models";
import {
  resetDeltaMode,
  resetExpandedGroups,
  resetSandbox,
  resetWorkingCategory,
  setAnimateDeltaModeIcons,
  setCursorLocation,
  setDeltaExpressions,
  setDeltaOriginalExpression,
  setExpandedGroups,
  setGlobalSearchMatches,
  setGroupExpansions,
  setSandbox,
  setUncategorizedCategory,
  setWorkingCategory,
} from "./working-category.actions";

export const workingCategoryFeatureKey = "workingCategory";

export interface WorkingCategoryState {
  categoryID: string;
  sandbox: Expression;
  uncategorized: Expression;
  deltaMode: DeltaModeStateObj;
  cursorLocation: CursorLocation;
  expandedGroups: { [groupId: string]: boolean };
  readOnly: boolean;
  globalSearchMatches: GlobalSearchMatch[];
}

// Ensure sandboxID and uncategorizedID are always the same
// via uuid.v3 which can generate consistent UUIDs from
// strings by providing a string and a "namespace"
const uuidNamespace = "0c6da875-7c38-4a6d-a07d-c8089ba3cdcc";
const sandboxID = uuidFromString(SpecialCategoryLabels.SANDBOX, uuidNamespace);
const uncategorizedID = uuidFromString(
  SpecialCategoryLabels.UNCATEGORIZED,
  uuidNamespace,
);

function sandbox() {
  return {
    type: OperatorType.OR,
    label: SpecialCategoryLabels.SANDBOX,
    id: sandboxID,
    children: [],
  };
}

export const initialWorkingCategoryState: WorkingCategoryState = {
  categoryID: sandboxID,
  sandbox: sandbox(),
  uncategorized: {
    type: OperandType.UNCATEGORIZED,
    label: SpecialCategoryLabels.UNCATEGORIZED,
    id: uncategorizedID,
    hashes: [],
    children: [],
  },
  deltaMode: {
    animateIcons: false,
    deltaOriginalExpression: null,
    deltaCurrentExpression: null,
    deltaPlusExpression: null,
    deltaMinusExpression: null,
  },
  cursorLocation: null,
  expandedGroups: {},
  readOnly: false,
  globalSearchMatches: [],
};

export const workingCategoryReducer = createReducer(
  initialWorkingCategoryState,
  on(resetWorkingCategory, (state) => ({
    ...initialWorkingCategoryState,
    categoryID: state.sandbox.id,
    sandbox: state.sandbox,
    deltaMode: state.deltaMode,
  })),
  on(setWorkingCategory, (state, { categoryID, readOnly }) => ({
    ...state,
    categoryID,
    readOnly,
    cursorLocation: initialWorkingCategoryState.cursorLocation,
    expandedGroups: initialWorkingCategoryState.expandedGroups,
  })),
  on(setSandbox, (state, { sandbox }) => ({
    ...state,
    sandbox: { ...sandbox, id: sandboxID },
  })),
  on(resetSandbox, (state) => ({
    ...state,
    sandbox: sandbox(),
  })),
  on(setAnimateDeltaModeIcons, (state, { animate }) => ({
    ...state,
    deltaMode: {
      ...state.deltaMode,
      animateIcons: animate,
    },
  })),
  on(setDeltaOriginalExpression, (state, { deltaOriginalExpression }) => ({
    ...state,
    deltaMode: {
      ...state.deltaMode,
      deltaOriginalExpression: deltaOriginalExpression,
    },
  })),
  on(setDeltaExpressions, (state, { deltaCurrentExpression }) => ({
    ...state,
    deltaMode: {
      ...state.deltaMode,
      deltaCurrentExpression: deltaCurrentExpression,
      deltaMinusExpression: {
        type: OperatorType.AND,
        children: [
          state.deltaMode.deltaOriginalExpression,
          {
            ...deltaCurrentExpression,
            negate: true,
          },
        ],
      },
      deltaPlusExpression: {
        type: OperatorType.AND,
        children: [
          {
            ...state.deltaMode.deltaOriginalExpression,
            negate: true,
          },
          deltaCurrentExpression,
        ],
      },
    },
  })),
  on(resetDeltaMode, (state) => ({
    ...state,
    deltaMode: initialWorkingCategoryState.deltaMode,
  })),
  on(setUncategorizedCategory, (state, { uncategorized }) => ({
    ...state,
    uncategorized,
  })),
  on(setExpandedGroups, (state, { expandedGroups }) => ({
    ...state,
    expandedGroups,
  })),
  on(resetExpandedGroups, (state) => ({
    ...state,
    expandedGroups: initialWorkingCategoryState.expandedGroups,
  })),
  on(setGroupExpansions, (state, { groupExpansions }) => ({
    ...state,
    expandedGroups: { ...state.expandedGroups, ...groupExpansions },
  })),
  on(setCursorLocation, (state, { cursorLocation }) => ({
    ...state,
    cursorLocation,
  })),
  on(setGlobalSearchMatches, (state, { globalSearchMatches }) => ({
    ...state,
    globalSearchMatches,
  })),
);
